// src/services/tokenService.js
import api from './api';

/**
 * Servicio centralizado para generar tokens temporales con scopes unificados
 * Mantiene retrocompatibilidad con la implementación existente
 */

/**
 * Configuración de tipos de token y sus scopes esperados
 */
const TOKEN_TYPE_CONFIG = {
  // Token para que el cliente complete su flujo de onboarding
  'onboarding': {
    description: 'Carga de documentos durante proceso de solicitud',
    requiredPermissions: ['document_upload'],
    requiredFields: ['customer_id', 'request_id'],
    defaultExpiry: 30,
    singleUse: true
  },
  
  // Token para que el cliente autogestione todo su proceso
  'customer_self_service': {
    description: 'Cliente autogestiona su solicitud de credito',
    requiredPermissions: ['customer_flow', 'document_upload', 'identity_validation', 'contract_signing'],
    requiredFields: ['customer_id', 'request_id'],
    defaultExpiry: 60,
    singleUse: false
  },
  
  // Token para acceder a documentos de una solicitud específica
  'request': {
    description: 'Acceso a documentos de una solicitud específica',
    requiredPermissions: ['document_upload', 'document_view'],
    requiredFields: ['request_id', 'customer_id'],
    defaultExpiry: 30,
    singleUse: false
  },
  
  // Token para que el cliente acceda a documentos de SU solicitud
  'customer': {
    description: 'Cliente accede a documentos de su propia solicitud',
    requiredPermissions: ['document_upload', 'document_view'],
    requiredFields: ['request_id', 'customer_id'],
    defaultExpiry: 15,
    singleUse: false
  },

  // Token para que el cliente vincule una tarjeta en una solicitud puntual
  'card_linking': {
    description: 'Cliente vincula una tarjeta desde su celular',
    requiredPermissions: ['card_linking'],
    requiredFields: ['customer_id'],
    defaultExpiry: 30,
    singleUse: false
  },
  
  // Token para vendedor acceda a todas las solicitudes de su sucursal
  'branch': {
    description: 'Vendedor de sucursal gestiona documentos de múltiples solicitudes',
    requiredPermissions: ['document_upload', 'document_view', 'requests_management'],
    requiredFields: [], // branch_id se infiere del usuario autenticado
    defaultExpiry: 120,
    singleUse: false
  }
};

/**
 * Clase para manejar tokens temporales de forma unificada
 */
export class TokenService {
  static buildScope(requiredPermissions = [], scopeData = {}) {
    const basePermissions = Array.isArray(requiredPermissions) ? requiredPermissions : [];

    if (Array.isArray(scopeData)) {
      return {
        permissions: Array.from(new Set([...basePermissions, ...scopeData]))
      };
    }

    const normalizedScopeData = scopeData && typeof scopeData === 'object' ? scopeData : {};
    const extraPermissions = Array.isArray(normalizedScopeData.permissions)
      ? normalizedScopeData.permissions
      : [];

    return {
      ...normalizedScopeData,
      permissions: Array.from(new Set([...basePermissions, ...extraPermissions]))
    };
  }
  
  /**
   * Genera un token temporal con scope unificado
   * @param {Object} config - Configuración del token
   * @param {string} config.type - Tipo de token (onboarding, request, customer, branch, customer_self_service, card_linking)
   * @param {number} [config.expires_in] - Tiempo de expiración en minutos
   * @param {Object} config.context - Contexto específico del token
   * @param {Object|Array<string>} [config.scope] - Contexto adicional del scope
   * @returns {Promise<Object>} Respuesta con token y datos
   */
  static async generateToken({ type, expires_in, context = {}, scope = {} }) {
    const tokenConfig = TOKEN_TYPE_CONFIG[type];
    
    if (!tokenConfig) {
      throw new Error(`Tipo de token no soportado: ${type}`);
    }
    
    // Validar que se proporcionen los campos requeridos
    const missingFields = tokenConfig.requiredFields.filter(field => 
      !context[field] && context[field] !== 0
    );
    
    if (missingFields.length > 0) {
      throw new Error(`Campos requeridos faltantes para token tipo '${type}': ${missingFields.join(', ')}`);
    }
    
    // Preparar el payload para el API
    const payload = {
      type,
      scope: this.buildScope(tokenConfig.requiredPermissions, scope),
      expires_in: expires_in || tokenConfig.defaultExpiry,
      single_use: tokenConfig.singleUse,
      ...context
    };
    
    try {
      const response = await api.post('/temporary-token/generate', payload);
      return response.data;
    } catch (error) {
      console.error(`Error generando token tipo '${type}':`, error);
      throw error;
    }
  }
  
  /**
   * Genera token para carga de documentos durante onboarding
   * @param {string} customerId - ID del cliente
   * @param {string} requestId - ID de la solicitud
   * @param {number} [expiresIn=30] - Tiempo de expiración en minutos
   * @returns {Promise<Object>} Respuesta con token
   */
  static async generateOnboardingToken(customerId, requestId, expiresIn = 30, scope = {}) {
    return this.generateToken({
      type: 'onboarding',
      expires_in: expiresIn,
      scope,
      context: {
        customer_id: customerId,
        request_id: requestId
      }
    });
  }
  
  /**
   * Genera token para autogestión completa del cliente
   * @param {string} customerId - ID del cliente
   * @param {string} requestId - ID de la solicitud
   * @param {number} [expiresIn=60] - Tiempo de expiración en minutos
   * @returns {Promise<Object>} Respuesta con token
   */
  static async generateCustomerSelfServiceToken(customerId, requestId, expiresIn = 60, scope = {}) {
    return this.generateToken({
      type: 'customer_self_service',
      expires_in: expiresIn,
      scope,
      context: {
        customer_id: customerId,
        request_id: requestId
      }
    });
  }
  
  /**
   * Genera token para acceso a documentos de una solicitud específica
   * @param {string} requestId - ID de la solicitud
   * @param {string} customerId - ID del cliente
   * @param {number} [expiresIn=30] - Tiempo de expiración en minutos
   * @returns {Promise<Object>} Respuesta con token
   */
  static async generateRequestToken(requestId, customerId, expiresIn = 30) {
    return this.generateToken({
      type: 'request',
      expires_in: expiresIn,
      context: {
        request_id: requestId,
        customer_id: customerId
      }
    });
  }
  
  /**
   * Genera token para que el cliente acceda a documentos de SU solicitud
   * @param {string} requestId - ID de la solicitud
   * @param {string} customerId - ID del cliente
   * @param {number} [expiresIn=15] - Tiempo de expiración en minutos
   * @returns {Promise<Object>} Respuesta con token
   */
  static async generateCustomerToken(requestId, customerId, expiresIn = 15) {
    return this.generateToken({
      type: 'customer',
      expires_in: expiresIn,
      context: {
        request_id: requestId,
        customer_id: customerId
      }
    });
  }

  /**
   * Genera token para que el cliente vincule una tarjeta en una solicitud puntual
   * @param {string|number} customerId - ID del cliente
   * @param {string|number} requestId - UID o ID de la solicitud
   * @param {number} [expiresIn=30] - Tiempo de expiración en minutos
   * @param {Object|Array<string>} [scope={}] - Scope adicional
   * @returns {Promise<Object>} Respuesta con token
   */
  static async generateCardLinkingToken(customerId, requestId, expiresIn = 30, scope = {}) {
    if (!customerId) {
      throw new Error('customerId es requerido para generar un token de vinculación de tarjeta');
    }

    if (!requestId && requestId !== 0) {
      throw new Error('requestId es requerido para generar un token de vinculación de tarjeta');
    }

    const normalizedRequestId = String(requestId);
    const requestContext = { request_id: normalizedRequestId };

    return this.generateToken({
      type: 'card_linking',
      expires_in: expiresIn,
      scope,
      context: {
        customer_id: customerId,
        ...requestContext
      }
    });
  }
  
  /**
   * Genera token para acceso de sucursal a múltiples solicitudes
   * @param {number} [expiresIn=120] - Tiempo de expiración en minutos
   * @returns {Promise<Object>} Respuesta con token
   */
  static async generateBranchToken(expiresIn = 120) {
    return this.generateToken({
      type: 'branch',
      expires_in: expiresIn,
      context: {} // branch_id se infiere automáticamente del usuario autenticado
    });
  }
  
  /**
   * Valida un token temporal
   * @param {string} token - Token a validar
   * @returns {Promise<Object>} Respuesta con datos del token
   */
  static async validateToken(token) {
    try {
      const response = await api.post('/temporary-token/validate', { token });
      return response.data;
    } catch (error) {
      console.error('Error validando token:', error);
      throw error;
    }
  }
  
  /**
   * Genera URL móvil para un token
   * @param {string} token - Token generado
   * @param {string} type - Tipo de token
   * @returns {string} URL móvil
   */
  static generateMobileUrl(token, type, queryParams = {}) {
    const baseUrl = window.location.origin;
    const queryString = new URLSearchParams(
      Object.entries(queryParams).filter(([, value]) => value !== undefined && value !== null && value !== '')
    ).toString();
    const withQuery = (path) => queryString ? `${path}?${queryString}` : path;
    
    // Determinar la ruta base según el tipo de token
    switch (type) {
      case 'onboarding':
      case 'customer_self_service':
        return withQuery(`${baseUrl}/m/onboarding/${token}`);
        
      case 'request':
      case 'customer':
      case 'branch':
      case 'card_linking':
        return withQuery(`${baseUrl}/m/docs/${token}`);
        
      default:
        throw new Error(`Tipo de token no soportado para URL móvil: ${type}`);
    }
  }
  
  /**
   * Obtiene la configuración de un tipo de token
   * @param {string} type - Tipo de token
   * @returns {Object|null} Configuración del token
   */
  static getTokenConfig(type) {
    return TOKEN_TYPE_CONFIG[type] || null;
  }
  
  /**
   * Lista todos los tipos de token disponibles
   * @returns {Array<string>} Lista de tipos de token
   */
  static getAvailableTokenTypes() {
    return Object.keys(TOKEN_TYPE_CONFIG);
  }
}

// Exportar también las funciones individuales para mantener compatibilidad
export const {
  generateOnboardingToken,
  generateCustomerSelfServiceToken,
  generateRequestToken,
  generateCustomerToken,
  generateCardLinkingToken,
  generateBranchToken,
  validateToken,
  generateMobileUrl,
  getTokenConfig,
  getAvailableTokenTypes
} = TokenService;

export default TokenService;
